import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import PolicyLayout from "./PolicyLayout";
import "../css/privacy.scss";

const Privacy = () => (
  <PolicyLayout
    eyebrow="Your information"
    title="Privacy policy"
    lede={`This policy explains how ${siteConfig.brandName} collects, uses, shares, retains, and protects personal data when you shop or contact us.`}
    nav={[
      { href: "#collect", label: "Collection" },
      { href: "#use", label: "Use" },
      { href: "#sharing", label: "Sharing" },
      { href: "#rights", label: "Your rights" },
      { href: "#contact", label: "Contact" },
    ]}
    contact={false}
  >
    <section className="policy-section" id="scope">
      <p className="editorial-page__section-label">01</p>
      <h2>Scope and who controls your data</h2>
      <p>
        This policy applies to personal data handled through this online store,
        checkout, customer-care communications, and order support. The operator
        of {siteConfig.brandName}, based in {siteConfig.businessLocation.countryShortName},
        determines how that data is used for the purposes described below.
        Independent payment providers, carriers, and linked services may also
        control data under their own privacy notices.
      </p>
    </section>

    <section className="policy-section" id="collect">
      <p className="editorial-page__section-label">02</p>
      <h2>Personal data we collect</h2>
      <div className="privacy-data-list">
        <article>
          <h3>Order details</h3>
          <p>
            Name, email address, delivery address, optional company, ordered
            items, amount, currency, order number, payment status, tracking, and
            related transaction records.
          </p>
        </article>
        <article>
          <h3>Support details</h3>
          <p>
            The contact details, messages, photographs, and documents you choose
            to provide for questions, returns, complaints, safety reports, or
            rights requests.
          </p>
        </article>
        <article>
          <h3>Device details</h3>
          <p>
            IP address, browser and device type, requested page, referring page,
            access time, and security or error logs produced by hosting and
            network systems.
          </p>
        </article>
        <article>
          <h3>Browser storage</h3>
          <p>
            Product identifiers, options, quantities, and saved-item identifiers
            stored locally under <code>ierryhouse:cart</code> and{" "}
            <code>ierryhouse:wishlist</code>. See the{" "}
            <Link to="/cookies">cookie policy</Link>.
          </p>
        </article>
      </div>
      <p>
        Payment-card numbers, security codes, and wallet credentials are entered
        on the payment provider&apos;s hosted page and are not requested by our
        checkout form. Please do not send them to us by email.
      </p>
    </section>

    <section className="policy-section" id="sources">
      <p className="editorial-page__section-label">03</p>
      <h2>Sources and whether data is required</h2>
      <p>
        We receive data directly from you, from your browser or device, and from
        providers involved in payment, fraud prevention, hosting, delivery, and
        customer service. Required checkout fields are needed to process and
        deliver an order; if they are not supplied, we may be unable to complete
        the purchase. Optional fields and ordinary customer-care messages are
        voluntary.
      </p>
    </section>

    <section className="policy-section" id="use">
      <p className="editorial-page__section-label">04</p>
      <h2>Why we use personal data</h2>
      <ul className="policy-list">
        <li>Process payment status, accept, pack, deliver, and support orders</li>
        <li>Send confirmations, tracking, service notices, and recall notices</li>
        <li>Answer questions and administer returns, refunds, and complaints</li>
        <li>Prevent fraud, misuse, security incidents, and payment disputes</li>
        <li>Maintain, debug, protect, and improve the store and checkout</li>
        <li>Meet accounting, tax, product-safety, and other legal obligations</li>
        <li>Establish, exercise, or defend legal claims</li>
      </ul>
      <p>
        These uses support your requested transaction, our legitimate operation
        of the store, compliance with law, or another lawful ground required in
        your location. We do not use your checkout email for promotional messages
        unless you separately and affirmatively agree. You may withdraw from
        direct marketing at any time without charge.
      </p>
    </section>

    <section className="policy-section" id="sharing">
      <p className="editorial-page__section-label">05</p>
      <h2>When data is disclosed</h2>
      <p>
        We disclose only the data reasonably needed to providers that support
        payment, fraud checks, hosting, security, delivery, communications,
        customer care, accounting, and professional advice. We may also disclose
        data to comply with law or a valid authority request, protect customers
        or others, investigate wrongdoing, or complete a genuine business
        reorganization subject to appropriate safeguards.
      </p>
      <p>
        We do not sell personal data and do not provide it to another party for
        that party&apos;s direct marketing. Providers must handle data for the
        relevant service and under applicable confidentiality, security, and
        retention obligations.
      </p>
    </section>

    <section className="policy-section" id="international">
      <p className="editorial-page__section-label">06</p>
      <h2>International processing</h2>
      <p>
        A payment, cloud, communications, or logistics provider may process data
        outside {siteConfig.businessLocation.countryShortName} or your country.
        Where applicable law requires it, we use contractual or other recognized
        safeguards and limit the information transferred to what is needed for
        the service.
      </p>
    </section>

    <section className="policy-section" id="retention">
      <p className="editorial-page__section-label">07</p>
      <h2>Retention and deletion</h2>
      <p>
        We keep personal data only for as long as reasonably needed for the
        purpose collected, including order performance, support, product-safety
        notices, accounting, fraud prevention, dispute resolution, and legal
        obligations. Retention depends on the record type, transaction status,
        limitation periods, and mandatory record-keeping rules. Data is deleted,
        anonymized, or securely isolated when no longer required.
      </p>
      <p>
        Your bag and saved items remain in your own browser until you remove them,
        clear site data, or browser settings remove them.
      </p>
    </section>

    <section className="policy-section" id="security">
      <p className="editorial-page__section-label">08</p>
      <h2>Security and data incidents</h2>
      <p>
        We use proportionate administrative, technical, and organizational
        measures intended to protect data against unauthorized or accidental
        access, use, alteration, loss, or disclosure. No online service can
        promise absolute security. If an incident occurs, we will investigate,
        limit harm, and notify affected people or authorities where required.
      </p>
    </section>

    <section className="policy-section" id="rights">
      <p className="editorial-page__section-label">09</p>
      <h2>Your choices and privacy rights</h2>
      <p>
        Subject to the law that applies to your request, you may ask whether we
        hold your personal data and request access, correction, deletion,
        restriction, objection, withdrawal of consent, or a portable copy. Some
        rights are limited where we must retain data, protect another person, or
        establish a legal claim.
      </p>
      <p>
        We may ask for enough information to verify identity and locate the
        relevant records. We will respond within the period required by
        applicable law. A lawful, cost-based fee may apply to a formal data access
        request where the law permits it, and we will explain that fee first.
      </p>
    </section>

    <section className="policy-section" id="children">
      <p className="editorial-page__section-label">10</p>
      <h2>Children&apos;s privacy</h2>
      <p>
        The store is intended for people able to enter a purchase contract. We do
        not knowingly collect a child&apos;s personal data without authorization from
        a parent, guardian, or other lawful basis where required. Contact us if
        you believe a child supplied personal data improperly so we can review
        and take appropriate action.
      </p>
    </section>

    <section className="policy-section" id="changes">
      <p className="editorial-page__section-label">11</p>
      <h2>Policy changes</h2>
      <p>
        We may update this policy when services, providers, practices, or legal
        requirements change. The date above identifies the current version.
        Material changes will be presented on the site or communicated by another
        appropriate method before they take effect where required.
      </p>
    </section>

    <section className="policy-section" id="contact">
      <p className="editorial-page__section-label">12</p>
      <h2>Privacy contact</h2>
      <p>
        Send privacy questions, direct-marketing opt-outs, and requests for
        access or correction to{" "}
        <a href={`mailto:${siteConfig.contact.email}`}>
          {siteConfig.contact.email}
        </a>
        . Use the subject line “Privacy request” and describe the records or
        activity concerned. Written requests may also be sent to our business
        address: {siteConfig.contact.address}, {siteConfig.businessLocation.countryName}.
        You may also contact the competent data-protection authority in your
        location where that right applies.
      </p>
    </section>
  </PolicyLayout>
);

export default Privacy;
